import { faEnvelopesBulk } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Banner, classnames, LoadingIndicator, Toast, Modal, ModalBody, ModalFooter, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { ButtonColor } from '@/components/Buttons/ButtonElement';
import React, { useEffect, useState } from 'react';
import { getAvailableRecipicants, MeetingType, sendMeetings } from '@/services/booking';
import { OptionalId, ProjectTag, TranslatableText } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useAsyncState } from '@/utils/hooks';
import { LetterSetupData } from '../../../MeetingOverview';
import { ResidentialUnitMeetingData } from '../../MeetingApartments/MeetingApartments';
import theme from './UnitsDialog.module.scss';
import { UnitSelector } from '@/seller/components/UnitSelector';

interface UnitsDialogProps {
    projectId: number;
    activeMeetingType: MeetingType;
    data: LetterSetupData;
    show: boolean;
    onSend(): void;
    onClose(): void;

    onCancel?(): void;
    confirmButtonColor?: ButtonColor;
    cancelButtonColor?: ButtonColor;
    confirmButtonText?: TranslatableText;
    cancelButtonText?: TranslatableText;
}

const UnitsDialog: React.FC<UnitsDialogProps> = ({
    projectId,
    activeMeetingType,
    data,
    show,
    onSend,
    onClose,
    onCancel,
    confirmButtonColor = 'primary',
    cancelButtonColor = 'tertiary',
    confirmButtonText = <Trans>Send</Trans>,
    cancelButtonText = <Trans>Cancel</Trans>,
}) => {
    const [showMore, setShowMore] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<OptionalId<ProjectTag>[]>([]);
    const [residentialUnitIds, setResidentialUnitIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const [availableUnits] = useAsyncState<number[] | null>(
        () => getAvailableRecipicants(projectId, activeMeetingType.id!),
        [activeMeetingType.id],
        null
    );

    useEffect(() => {
        if (availableUnits) {
            setLoading(false);
        }
    }, [availableUnits]);

    useEffect(() => {
        if (residentialUnitIds) {
            if (residentialUnitIds.filter((x) => !availableUnits!.includes(x)).length > 0) {
                Toast.warn(<Trans>This unit has invalid meetings</Trans>);
                setResidentialUnitIds(residentialUnitIds.filter((x) => availableUnits!.includes(x)));
            }
        }
    }, [residentialUnitIds]);

    const handleAcceptClick = async () => {
        if (residentialUnitIds.length === 0) {
            Toast.warn(<Trans>You have to select at least one unit</Trans>);
            return;
        }
        try {
            setSaving(true);
            await sendMeetings(data.projectId!, data.meetingType!.id!, residentialUnitIds);
            Toast.success(<Trans>Invitations successfully sent</Trans>);
        } catch {
            Toast.error(<Trans>Something went wrong while sending.</Trans>);
        } finally {
            setSaving(false);
            handleShowMore(false);
            triggerHandler(onSend);
            triggerHandler(onClose);
        }
    };

    const handleCancelClick = () => {
        handleShowMore(false);
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };
    const handleShowMore = (showMore) => {
        setShowMore(showMore);
    };
    const handleRemoveRow = (row: ResidentialUnitMeetingData) => {};
    const handleFilterChange = (unitId) => {
        handleShowMore(false);
    };

    return (
        <Modal isOpen={show} className={theme.rounded} toggle={handleCancelClick}>
            <ModalHeader>
                <FontAwesomeIcon size="2x" icon={faEnvelopesBulk} />
                <div className={theme.marginTop}>
                    {activeMeetingType.name} <Trans>letter</Trans>
                </div>
            </ModalHeader>
            <ModalBody>
                {loading || availableUnits === null ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <div className={theme.marginTop}>
                            <Trans>Select what units to send the letter to.</Trans>
                            {!availableUnits ||
                                (availableUnits.length === 0 && (
                                    <Banner type="info">
                                        <Trans>
                                            There are no available units with meetings set. <br />
                                            Please make sure the meetings and the connected meetings have date and time set.
                                        </Trans>
                                    </Banner>
                                ))}
                        </div>
                        <div className={classnames(theme.marginTop, theme.reminderContainer)}>
                            <UnitSelector
                                projectId={data.projectId!}
                                filter={(unit) => availableUnits.includes(unit.id)}
                                // TODO: See if this is needed at all. Not really sure what it does.
                                // selectedTags={selectedTags}
                                selectedResidentialUnitIds={residentialUnitIds}
                                onChange={setResidentialUnitIds}
                            />
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color={cancelButtonColor} onClick={handleCancelClick} disabled={saving}>
                    {cancelButtonText}
                </Button>
                <Button color={confirmButtonColor} onClick={handleAcceptClick} loading={saving}>
                    {confirmButtonText}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default UnitsDialog;
